import React, {
	FC,
	useCallback,
	useEffect,
	useRef,
	useState,
	useMemo,
} from "react";
import {
	Grid,
	GridCellsSettings,
	GridColumn,
	GridColumnProps,
	GridCustomCellProps,
	GridCustomHeaderCellProps,
	GridDataStateChangeEvent,
	GridDetailRowProps,
	GridFilterOperators,
	GridGroupableSettings,
	GridRowProps,
	GridSortSettings,
} from "@progress/kendo-react-grid";
import { GridPDFExport } from "@progress/kendo-react-pdf";
import {
	ExcelExport,
	ExcelExportProps,
} from "@progress/kendo-react-excel-export";

import { OverLayLoading } from "ui/Loading";
import {
	Pager,
	PagerProps,
	setExpandedState,
	setGroupIds,
} from "@progress/kendo-react-data-tools";
import {
	AggregateDescriptor,
	CompositeFilterDescriptor,
	DataResult,
	GroupDescriptor,
	process,
	SortDescriptor,
	State,
} from "@progress/kendo-data-query";
import { loadMessages } from "@progress/kendo-react-intl";

import { t } from "i18next";

import { handleFilteredItem, handleGroupEmptyMessage } from "utils/statUtils";
import { CustomRowRender, FilterHeaderCellRender } from "./CustomGrid";
import { CustomError } from "interface";
import { setError } from "store/error";
import { messagesEn, messagesNo } from "./utilities/localization";
import { COLOR } from "utils/constant";
import { filterIcon } from "@progress/kendo-svg-icons";

const unUsedHeight = 370; // 370px is the height of the filter and the header
loadMessages(messagesEn, "en");
loadMessages(messagesNo, "no");

// Memoize the groupable defaults
const DEFAULT_GROUPABLE = { footer: "visible", enabled: false };
const DEFAULT_PAGER_SIZES = [15, 30, 50, 100, 250, 500, 750, 1000];
const DEFAULT_ROW_HANDLER = () => COLOR.Grey20;

interface GridPageProps {
	filter?: any;
	dataKey?: string;
	isExpandable?: boolean;
	forceUpdate?: boolean;
	cells?: GridCellsSettings;
	aggregateFields?: AggregateDescriptor[];
	groupHeaderCell?: React.ComponentType<GridCustomCellProps>;
	group?: GroupDescriptor[];
	groupLargeColumn?: { field: string; width: number }[];
	defaultAggregates?: AggregateDescriptor[];
	showSelectedFilters?: boolean;
	headerCellRender?: React.ComponentType<GridCustomHeaderCellProps>;
	pageSizes?: number[];
	pageSize?: number;
	groupable?: GridGroupableSettings | boolean;
	pageable?: boolean;
	className?: string;
	apiMethod?: (filter: any) => Promise<any>;
	serverFilter?: boolean;
	filterOperators?: GridFilterOperators;
	filterable?: boolean;
	serverFilterMap?: {
		/// The field name that the server expects
		serverFieldName: string;
		/// The filter field on the grid
		filterFieldName: string;
	}[];
	handleStateChange?: (state: State) => void;
	defaultFilter?: CompositeFilterDescriptor;
	exportGridAsPDFRef?: React.RefObject<GridPDFExport>;
	exportGridAsExcelRef?: React.RefObject<ExcelExport>;
	excelProps?: Partial<ExcelExportProps>;
	gridColumns: GridColumnProps[];
	gridStyle?: React.CSSProperties;
	itemAboveHeight?: number;
	gridData?: any[];
	loading?: boolean;
	detailPage?: React.ComponentType<GridDetailRowProps>;
	gridHeight?: number;
	gridMaxHeight?: number;
	sortable?: GridSortSettings;
	defaultSort?: SortDescriptor[];
	handelShowRowBgColor?: (rowData: GridRowProps) => string;
	handelShowRowTextColor?: (rowData: GridRowProps) => string;
	highlightFilterColumns?: boolean;
	useDefaultRowTextColor?: boolean;
	hideVirtualScroll?: boolean;
	lastColBtns?: GridColumnProps;
	onFilteredDataChange?: (filteredData: any[]) => void;
}

const GridWithGroupPaging: FC<GridPageProps> = ({
	dataKey = "",
	filter,
	exportGridAsExcelRef,
	exportGridAsPDFRef,
	isExpandable = true,
	gridHeight = 900,
	itemAboveHeight = 0,
	handelShowRowBgColor,
	aggregateFields = [],
	forceUpdate,
	gridData,
	groupLargeColumn = [],
	showSelectedFilters = false,
	pageable = true,
	pageSizes = DEFAULT_PAGER_SIZES,
	pageSize = 15,
	groupable = DEFAULT_GROUPABLE,
	serverFilter = false,
	serverFilterMap = [],
	sortable,
	defaultSort,
	filterable = false,
	hideVirtualScroll = false,
	handelShowRowTextColor = DEFAULT_ROW_HANDLER,
	cells,
	...props
}) => {
	const [result, setResult] = useState<DataResult>({
		data: gridData || [],
		total: gridData?.length || 0,
	});
	const [loading, setLoading] = useState<boolean>(false);
	const dataRef = useRef<any[]>(gridData || []);

	// Only recalculate when filter actually changes
	const filterKey = useMemo(() => JSON.stringify(filter ?? {}), [filter]);

	const [dataState, setDataState] = React.useState<State & any>({
		...(pageable
			? {
					skip: 0,
					take: pageSize,
					pageSize: pageSize,
				}
			: {}),
		sort: defaultSort,
		group: props.group,
	});

	const [pageHeight, setPageHeight] = useState(window.innerHeight);
	const [collapsedState] = React.useState<string[]>([]);
	const [selectedFilters, setSelectedFilters] = React.useState("");

	const overlayRef = useRef<HTMLDivElement>(null);
	const filterRef = useRef<HTMLDivElement>(null);
	const filterHeightRef = useRef(0);
	const internalFilter = useRef<any>(null);
	const gridRef = useRef<HTMLDivElement>(null);
	const apiMethod = props.apiMethod;
	const persistedFilterRef = useRef<any>(null);

	useEffect(() => {
		const updateHeight = () => setPageHeight(window.innerHeight);
		window.addEventListener("resize", updateHeight);

		return () => window.removeEventListener("resize", updateHeight);
	}, []);

	useEffect(() => {
		if (!gridData) return;
		
		// Lagre gjeldende filter
		if (dataState.filter) {
			persistedFilterRef.current = dataState.filter;
		}

		const __dataSate = {
			skip: 0,
			take: pageable ? dataState.take : gridData.length,
			pageSize: pageable ? dataState.pageSize : gridData.length,
			filter: persistedFilterRef.current, // Bruker lagret filter
			sort: dataState.sort,
			group: dataState.group,
		};
		setDataState(__dataSate);
		const newDataState = process(gridData, __dataSate);
		setGroupIds({ data: newDataState.data, group: dataState.group });
		setResult(newDataState);
		dataRef.current = gridData;
		props.onFilteredDataChange?.(newDataState.data);
	}, [gridData, forceUpdate]);

	useEffect(() => {
		if (dataState.filter) {
			if (handleFilteredItem) {
				const filters = handleFilteredItem(dataState.filter);
				const filterItems = filters.map((filter) => {
					const col = props.gridColumns.find(
						(col) => col.field === filter.field,
					);
					let field = filter.field;
					if (col && col.title) {
						field = col.title;
					}
					return ` ${field} : [${filter.values.map((v: any) => `"${v}"`)}]  `;
				});

				setSelectedFilters(filterItems.toString());
			}
			if (filterRef.current) {
				filterHeightRef.current = filterRef.current.clientHeight;
			}
		}
	}, [dataState.filter]);

	useEffect(() => {
		handleGroupEmptyMessage(dataState);
	}, [dataState.filter]);

	// Only create new callback when apiMethod or filter actually changes
	const handleApiRequest = useCallback(() => {
		if (!apiMethod) {
			console.error("No API method provided for data fetching.");
			return;
		}

		let newFilter = {
			...(filter ?? {}),
		};

		if (serverFilter && internalFilter.current) {
			newFilter = {
				...newFilter,
				...dataState,
				...internalFilter.current,
			};
		}
		setLoading(true);

		apiMethod(newFilter)
			.then((data) => {
				dataRef.current = data || [];

				const newDataState = process(data || [], dataState);
				setGroupIds({ data: newDataState.data, group: dataState.group });
				setResult(newDataState);
				props.onFilteredDataChange?.(data || []);
			})
			.catch((error) => {
				console.error("Error fetching data:", error);
				if (error.response?.data) {
					setError(new CustomError("Something went wrong", "error"));
				} else {
					setError(new CustomError(error.message));
				}
			})
			.finally(() => {
				setLoading(false);
			});
	}, [apiMethod, filter, serverFilter, dataState, props]);

	// Only fetch when filter key changes - not on callback recreation
	useEffect(() => {
		if (!apiMethod) return;
		handleApiRequest();
	}, [apiMethod, filterKey, forceUpdate]);

	const handleDataStateChange = (dataState: State) => {
		if (!gridData) {
			return {
				data: [],
				total: 0,
			};
		}

		const newDataState = process(gridData, dataState);
		setGroupIds({ data: newDataState.data, group: dataState.group });

		return newDataState;
	};

	const onDataStateChange = useCallback((e: GridDataStateChangeEvent) => {
		e.dataState.group = e.dataState.group?.map((group) => ({
			...group,
			aggregates: group.aggregates
				? [...aggregateFields, ...group.aggregates]
				: aggregateFields,
		}));
		let data = handleDataStateChange(e.dataState);
		setResult(data);
		handleFilterMap(e.dataState);
		setDataState(e.dataState);

		if (props.onFilteredDataChange && gridData) {
			const filteredResult = process(gridData, { filter: e.dataState.filter });
			props.onFilteredDataChange(filteredResult.data);
		}
	}, [aggregateFields, gridData, props]);

	const newData = setExpandedState({
		data: result.data,
		collapsedIds: collapsedState,
	});

	const handleFilterMap = (state: State) => {
		if (props.handleStateChange) {
			props.handleStateChange(state);
		}
		if (!state.filter) {
			return;
		}

		setDataState(state);
		if (handleFilteredItem) {
			const filters = handleFilteredItem(state.filter);

			let mapObject: any = {};

			filters.forEach((filter) => {
				const map = serverFilterMap.find(
					(map) => map.filterFieldName === filter.field,
				);
				if (map) {
					mapObject[map.serverFieldName] = filter.values[0];
				}
			});

			internalFilter.current = mapObject;
			if (props.apiMethod) {
				handleApiRequest();
			}
		}
	};

	const currentHeight =
		pageHeight - unUsedHeight - itemAboveHeight - filterHeightRef.current * 2;

	// Memoize row handler
	const rowHandler = useMemo(
		() => ({
			data: (row: any) =>
				CustomRowRender(
					row,
					handelShowRowBgColor,
					handelShowRowTextColor,
				),
		}),
		[handelShowRowBgColor, handelShowRowTextColor]
	);

	// Memoize grid style
	const gridStyleMemo = useMemo(
		() => ({
			height: currentHeight,
			paddingBottom: "30px",
			overflowY: "auto" as const,
			width: "100%",
			maxHeight: currentHeight,
			...(props.gridStyle || {}),
		}),
		[currentHeight, props.gridStyle]
	);

	// Memoize cell config
	const cellConfig = useMemo(
		() => ({
			headerCell: FilterHeaderCellRender,
			group: {
				groupHeader: props.groupHeaderCell,
			},
			...cells,
		}),
		[props.groupHeaderCell, cells]
	);

	return (
		<div style={{}}>
			<div ref={overlayRef} style={{}}>
				<OverLayLoading show={loading || props?.loading === true} />
			</div>

			{showSelectedFilters && (
				<div ref={filterRef}>Filter: {selectedFilters}</div>
			)}

			<div
				style={{
					marginTop: 30,
					overflowX: "auto",
				}}
				ref={gridRef}
			>
				<ExcelExport
					data={process(dataRef.current, { group: dataState.group }).data}
					group={dataState.group}
					collapsible={true}
					{...(props?.excelProps || {})}
					ref={exportGridAsExcelRef}
				>
					<Grid
						style={gridStyleMemo}
						{...dataState}
						rowHeight={34}
						sortable={sortable}
						data={newData}
						columnMenuIcon={filterIcon}
						rows={rowHandler}
						pageSize={dataState.pageSize}
						onDataStateChange={onDataStateChange}
						className={` ${props.className} ${
							hideVirtualScroll ? "hide-virtual-scroll" : ""
						}`}
						pageable={pageable}
						total={pageable ? result.total : undefined}
						pager={(prop) => <MyPager {...prop} pageSizes={pageSizes} />}
						group={dataState.group}
						groupable={groupable}
						cells={cellConfig}
						{...props}
					>
						{props.gridColumns.map((column, key) => {
							if (
								dataState.group &&
								dataState.group.length > 0 &&
								groupLargeColumn.length > 0
							) {
								const groupColumn = groupLargeColumn.find(
									(col) => col.field === column.field,
								);

								if (groupColumn) {
									return (
										<GridColumn
											key={key}
											{...column}
											width={groupColumn.width}
										/>
									);
								}
							}

							return <GridColumn key={key} {...column} />;
						})}
						{props.lastColBtns && <GridColumn {...props.lastColBtns} />}
					</Grid>
				</ExcelExport>
			</div>
		</div>
	);
};

export default GridWithGroupPaging;

const MyPager = (props: PagerProps) => {
	return (
		<div style={{}}>
			<Pager
				responsive={true}
				skip={props.skip}
				take={props.take}
				total={props.total}
				onPageChange={props.onPageChange}
				buttonCount={10}
				info={true}
				previousNext={true}
				messagesMap={messagesMap}
				type="numeric"
				pageSizes={props.pageSizes}
			/>
		</div>
	);
};

enum MessageKey {
	PagerFirstPage = "pager.firstPage",
	PagerPreviousPage = "pager.previousPage",
	PagerNextPage = "pager.nextPage",
	PagerLastPage = "pager.lastPage",
	PagerInfo = "pager.info",
	PagerAriaKeyshortcuts = "pager.ariaKeyshortcuts",
	PagerPage = "pager.page",
	PagerOf = "pager.of",
	PagerTotalPages = "pager.totalPages",
	PagerMobileSelect = "pager.mobileSelect",
	PagerMoreButtonLabel = "pager.moreButtonLabel",
	PagerItemsPerPage = "pager.itemsPerPage",
	PagerPageSizeAriaLabel = "pager.pageSizeAriaLabel",
}

const messagesMap = (key: string) => {
	switch (key) {
		case MessageKey.PagerInfo:
			return {
				messageKey: key,
				defaultMessage: `{0} ${t("To")} {1} ${t("Of")} {2} ${t("Lines")}`,
			};
		case MessageKey.PagerFirstPage:
			return { messageKey: key, defaultMessage: "First page" };
		case MessageKey.PagerPreviousPage:
			return { messageKey: key, defaultMessage: "Previous page" };
		case MessageKey.PagerNextPage:
			return { messageKey: key, defaultMessage: "Next page" };
		case MessageKey.PagerLastPage:
			return { messageKey: key, defaultMessage: "Last page" };
		case MessageKey.PagerItemsPerPage:
			return { messageKey: key, defaultMessage: `${t("PagerItems")}` };
		default:
			return { messageKey: key, defaultMessage: "" };
	}
};
